import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const articleUrl = article ? siteUrl + '/article/' + article.slug : '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = article.metaTitle;
    } else {
      document.title = 'Article Not Found - Sugargoo Spreadsheet';
    }
  }, [article]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center pt-24 pb-16 px-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            Back to Homepage
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.image,
            author: {
              '@type': 'Organization',
              name: 'Sugargoo Spreadsheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Sugargoo Spreadsheet',
              logo: {
                '@type': 'ImageObject',
                url: siteUrl + '/favicon.ico',
              },
            },
            datePublished: '2026-08-01',
            dateModified: '2026-08-05',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': articleUrl,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.category,
                item: siteUrl + '/article/' + article.slug,
              },
            ],
          }),
        }}
      />
      <main>
      <Navbar />

      <article>
        <header className="relative pt-24 pb-16 md:pt-28 md:pb-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700 font-medium">{article.category}</span>
              <span className="mx-2">/</span>
              <span className="text-foreground-700 font-medium truncate max-w-[200px] inline-block align-bottom">{article.title}</span>
            </nav>

            <span className="inline-block bg-accent-100 text-accent-800 text-xs font-semibold px-3 py-1 rounded-full mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight text-balance mb-5">
              {article.title}
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 leading-relaxed max-w-3xl mb-6">
              {article.summary}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-calendar-line"></i> Updated August 2026
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-book-open-line"></i> Sugargoo Spreadsheet
              </span>
            </div>
          </div>
        </header>

        <div className="relative max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="prose-custom">

            {/* Featured Image */}
            <div className="mb-12 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-auto"
                title={article.title}
              />
            </div>

            {/* Table of Contents */}
            <nav className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-12" aria-label="Table of Contents">
              <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Table of Contents</h2>
              <ol className="space-y-2 text-sm">
                {article.sections.map((section, i) => (
                  <li key={i} className="text-foreground-600 hover:text-primary-600 transition-colors">
                    <a href={`#section-${i + 1}`} className="flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-semibold flex-shrink-0">{i + 1}</span>
                      {section.heading}
                    </a>
                  </li>
                ))}
                {article.faqs.length > 0 && (
                  <li className="text-foreground-600 hover:text-primary-600 transition-colors">
                    <a href="#faq-section" className="flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-semibold flex-shrink-0">{article.sections.length + 1}</span>
                      Frequently Asked Questions
                    </a>
                  </li>
                )}
                <li className="text-foreground-600 hover:text-primary-600 transition-colors">
                  <a href="#related-articles" className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-semibold flex-shrink-0">{article.sections.length + (article.faqs.length > 0 ? 2 : 1)}</span>
                    Related Articles & Next Steps
                  </a>
                </li>
              </ol>
            </nav>

            {/* Content Sections */}
            {article.sections.map((section, i) => (
              <section key={i} id={`section-${i + 1}`} className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, j) => (
                  <p key={j} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            {/* FAQ Section */}
            {article.faqs.length > 0 && (
              <section id="faq-section" className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">Frequently Asked Questions</h2>
                <div className="space-y-5">
                  {article.faqs.map((faq, i) => (
                    <details key={i} className="group bg-background-100 border border-background-200/70 rounded-lg overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-semibold text-foreground-900 cursor-pointer hover:text-primary-600 transition-colors">
                        {faq.q}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* Related Articles */}
            <section id="related-articles" className="mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">Continue Reading</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group flex items-start gap-3 bg-background-100 border border-background-200/70 rounded-lg p-4 hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.imageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-semibold text-accent-700 bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {related.category}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors mt-1.5 line-clamp-2 leading-snug">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>

            {/* Back to Home */}
            <div className="border-t border-background-200/70 pt-8 mt-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
            </div>
          </div>
        </div>

        {/* Inline CTA */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8 text-center">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">Ready to Start Exploring?</h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Browse thousands of curated products across all categories on the main platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 text-background-50 px-7 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                Sugargoo Spreadsheet
              </a>
              <Link
                to="/"
                className="border border-background-300/60 text-foreground-700 px-7 py-3 rounded-md text-sm font-semibold hover:bg-background-200/50 transition-all whitespace-nowrap"
              >
                Browse Categories
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
    </>
  );
}